"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback } from "react";
import { useGeminiRealtimeCall } from "@/hooks/use-gemini-realtime-call";
import { useRealtimeCall } from "@/hooks/use-realtime-call";
import type { RealtimeTranscriptEntry } from "@/hooks/use-realtime-call";
export type { RealtimeCallStatus as VoiceCallStatus, RealtimeTranscriptEntry as VoiceCallTranscriptEntry, RealtimeUsageTotals as VoiceCallUsageTotals } from "@/hooks/use-realtime-call";

/**
 * Providers whose realtime sessions speak their own wire protocol rather than
 * OpenAI's. The gateway proxies each protocol natively, so the browser client
 * has to match the provider serving the selected model.
 */
const GEMINI_PROVIDER_ID = "google-ai-studio";
interface UseVoiceCallOptions {
  model: string | null;
  voice: string | null;
  /**
   * Provider id of the catalogue mapping serving the selected model. Null
   * falls back to the OpenAI protocol.
   */
  provider: string | null;
  onCallError?: (message: string) => void;
}

/**
 * Single entry point for the voice-call UI. Both provider hooks are invoked
 * unconditionally — hooks cannot be called conditionally — but only the one
 * matching the selected model's provider is ever started, and its state and
 * actions are what this facade returns. The idle hook holds no microphone,
 * socket or timer.
 */
export function useVoiceCall(t0) {
  const $ = _c(16);
  const {
    model,
    voice,
    provider,
    onCallError
  } = t0;
  const isGemini = provider === GEMINI_PROVIDER_ID;
  const t1 = isGemini ? null : model;
  let t2;
  if ($[0] !== onCallError || $[1] !== t1 || $[2] !== voice) {
    t2 = {
      model: t1,
      voice,
      onCallError
    };
    $[0] = onCallError;
    $[1] = t1;
    $[2] = voice;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const openaiCall = useRealtimeCall(t2);
  const t3 = isGemini ? model : null;
  let t4;
  if ($[4] !== onCallError || $[5] !== t3 || $[6] !== voice) {
    t4 = {
      model: t3,
      voice,
      onCallError
    };
    $[4] = onCallError;
    $[5] = t3;
    $[6] = voice;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const geminiCall = useGeminiRealtimeCall(t4);
  const startOpenai = openaiCall.start;
  const startGemini = geminiCall.start;
  let t5;
  if ($[8] !== isGemini || $[9] !== startGemini || $[10] !== startOpenai) {
    t5 = seed => {
      if (isGemini) {
        startGemini();
      } else {
        startOpenai(seed);
      }
    };
    $[8] = isGemini;
    $[9] = startGemini;
    $[10] = startOpenai;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const start = t5;
  const active = isGemini ? geminiCall : openaiCall;
  const t6 = !isGemini;
  let t7;
  if ($[12] !== active || $[13] !== start || $[14] !== t6) {
    t7 = {
      ...active,
      start,
      supportsResume: t6
    };
    $[12] = active;
    $[13] = start;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  return t7;
}